import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCompany } from "../../../hooks/useCompany"; 
import { Table, Button, Radio, Input, Space, ConfigProvider } from "antd";
import type { ColumnType } from 'antd/es/table';
import CardHeader from "../../../components/Shared/CardHeader";
import { api } from '../../../services/axios';
import { toast } from "react-toastify";
import Loader from "../../../components/Shared/Loader";
import './styles.css';
import { SearchOutlined } from '@ant-design/icons';
interface ExpenditureData {
    key: string;
    category: string;
    subcategory: string;
    totalValue: string;
    attention: string;
    isTotalRow?: boolean;
    isSubcategory?: boolean;
    numericValue?: number;
    macroStages: { [key: string]: string };
}

export default function ProjectsDetails() {
    const [loading, setLoading] = useState(false);
    const { company } = useCompany();
    const { serviceId, projectId } = useParams<{ serviceId: string; projectId: string }>();
    const resolvedServiceId = serviceId || company.serviceId;
    const [expenditures, setExpenditures] = useState<ExpenditureData[]>([]);
    const [viewMode, setViewMode] = useState<'complete' | 'simplified'>('complete');
    const [dynamicColumns, setDynamicColumns] = useState<ColumnType<ExpenditureData>[]>([]);

    useEffect(() => {
        fetchExpenditureData();
    }, [resolvedServiceId, projectId]);

    async function fetchExpenditureData() {
        try {
            setLoading(true);

            if (!projectId) {
                toast.error('Projeto não informado na rota.');
                return;
            }
          
            const response = await api.get(`Expenditure/GetSummaryByService/${resolvedServiceId}?projectId=${projectId}`);
            const summaryData: ExpenditureData[] = response.data.data || [];

            if (summaryData.length > 0) {
                const uniqueMacroStageKeys: string[] = [
                    ...new Set(summaryData.flatMap((row: ExpenditureData) => row.macroStages ? Object.keys(row.macroStages) : []))
                ];

                uniqueMacroStageKeys.sort((a, b) => {
                    const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
                    const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
                    return numA - numB;
                });

                const newDynamicColumns: ColumnType<ExpenditureData>[] = uniqueMacroStageKeys.map((stageName: string) => ({
                    title: (
                        <div style={{ backgroundColor: '#EFFBFE', padding: '8px' }}>
                            {stageName}
                        </div>
                    ),
                    dataIndex: ['macroStages', stageName],
                    key: stageName,
                    render: (text: string) => (
                        <div style={{ padding: '8px' }}>{text || 'R$ 0,00'}</div> 
                    )
                }));
                setDynamicColumns(newDynamicColumns);
            }

            setExpenditures(summaryData);
        } catch (error) {
            toast.error('Erro ao carregar dados de dispêndio');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const baseColumns: ColumnType<ExpenditureData>[] = [
        {
            title: (
                <div style={{ backgroundColor: '#EFFBFE', padding: '8px' }}>
                    Projetos
                </div>
            ),
            dataIndex: 'category',
            key: 'category',
            render: (text: string, record: ExpenditureData) => ({
                children: (
                    <div style={{
                        backgroundColor: '#EFFBFE',
                        padding: '8px',
                        fontWeight: record.isTotalRow ? 'bold' : 'normal',
                        paddingLeft: record.isSubcategory ? '32px' : '8px',
                    }}>
                        {record.isTotalRow ? text : record.subcategory}
                    </div>
                ),
                props: {
                    style: {
                        background: '#EFFBFE',
                        borderRight: '1px solid #f0f0f0'
                    }
                }
            }),
            onCell: () => ({
                style: {
                    background: '#EFFBFE',
                    borderRight: '1px solid #f0f0f0'
                }
            })
        },
        {       
            title: (
                <div style={{ backgroundColor: '#EFFBFE', padding: '8px' }}>
                    Valor Total
                </div>
            ),
            dataIndex: 'totalValue',
            key: 'totalValue',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: {
                setSelectedKeys: (selectedKeys: React.Key[]) => void;
                selectedKeys: React.Key[];
                confirm: () => void;
                clearFilters?: () => void;
            }) => (
                <div style={{ backgroundColor: '#EFFBFE', padding: 8 }}>
                    <Input
                        placeholder="Filtrar valores"
                        value={selectedKeys[0] as string}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Filtrar
                        </Button>
                        <Button
                            onClick={() => clearFilters?.()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: string | number | boolean, record: ExpenditureData) => {
                const numericValue = record.numericValue || 0;
                const filterValue = parseFloat(value.toString().replace(/[^\d,]/g, '').replace(',', '.'));
                return numericValue >= filterValue;
            },
            sorter: (a: ExpenditureData, b: ExpenditureData) => (a.numericValue || 0) - (b.numericValue || 0),
            render: (text: string, record: ExpenditureData) => ({
                children: (
                    <div style={{
                        padding: '8px',
                        fontWeight: record.isTotalRow ? 'bold' : 'normal',
                    }}>{text}</div>
                ),
                props: {
                    style: {
                        borderRight: '1px solid #f0f0f0'
                    }
                }
            })
        },
        {
                title: (
                    <div style={{ backgroundColor: '#EFFBFE', padding: '8px' }}>
                        Atenção
                    </div>
                ),
            dataIndex: 'attention',
            key: 'attention',
            render: (text: string) => (
                <div style={{ padding: '8px' }}>{text}</div>
            )
        },
    ];

      
    const columns = [
        baseColumns[0], baseColumns[1], ...dynamicColumns
    ];

    const filteredData = viewMode === 'simplified'
        ? expenditures.filter(item => item.isTotalRow)
        : expenditures;

    return (
        <>
            {loading && <Loader />}
            <div className="card">
                <CardHeader
                    pageName={"Visão Projetos"}
                    hasPageBefore={true}
                        linkPageBefore={`/${resolvedServiceId}/summaryOfValues`}
                    pageBeforeName="Identificação Técnica"
                />
                <div style={{
                    margin: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <ConfigProvider
                        theme={{
                            components: {
                                Radio: {
                                    buttonSolidCheckedActiveBg: '#0000A4',
                                    buttonSolidCheckedBg: '#0000A4',
                                    buttonSolidCheckedHoverBg: '#0000A4',
                                    colorPrimary: '#0000A4',
                                    colorPrimaryHover: '#0000A4'
                                }
                            }
                        }}
                    >
                        <Radio.Group
                            value={viewMode}
                            onChange={(e) => setViewMode(e.target.value)}
                            style={{ marginBottom: 16 }}
                            buttonStyle="solid"
                        >
                            <Radio.Button
                                value="complete"
                                style={{ color: viewMode === 'complete' ? 'white' : '#0000A4' }}
                            >
                                <span style={{ color: viewMode === 'complete' ? 'white' : 'gray' }}>Completo</span>
                            </Radio.Button>
                            <Radio.Button
                                value="simplified"
                                style={{ color: viewMode === 'simplified' ? 'white' : '#0000A4' }}
                            >
                                <span style={{ color: viewMode === 'simplified' ? 'white' : 'gray' }}>Simplificado</span>
                            </Radio.Button>
                        </Radio.Group>
                    </ConfigProvider>
                </div>
                <div style={{ margin: '16px' }}>
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        bordered
                        pagination={false}
                        rowClassName={(record: ExpenditureData) =>
                            record.isTotalRow ? 'total-row' : record.isSubcategory ? 'subcategory-row' : 'category-row'
                        }
                    />
                </div>
            </div>
        </>
    );
}